import { useEffect } from "react";

/**
 * Minimal head-tag manager (no react-helmet dependency).
 * Updates <title>, description, OpenGraph, and canonical on mount,
 * restoring the previous values on unmount so SPA navigation stays clean.
 *
 * Usage:
 *   useSEO({
 *     title: "Houston Apartment Search | Urban Nest Quest",
 *     description: "Search thousands of Houston apartments...",
 *     keywords: ["Houston Apartments", "Apartment Finder Houston"],
 *     path: "/apartment-search",
 *   });
 */
export const useSEO = ({ title, description, keywords = [], path = "" }) => {
  const keywordsStr = keywords.join(", ");
  useEffect(() => {
    const prev = {
      title: document.title,
      desc: document.querySelector('meta[name="description"]')?.content,
      keys: document.querySelector('meta[name="keywords"]')?.content,
      og:   document.querySelector('meta[property="og:title"]')?.content,
      ogd:  document.querySelector('meta[property="og:description"]')?.content,
      canon: document.querySelector('link[rel="canonical"]')?.href,
    };

    if (title) document.title = title;

    const upsertMeta = (selector, attr, key, value) => {
      let el = document.querySelector(selector);
      if (!el) {
        el = document.createElement("meta");
        el.setAttribute(attr, key);
        document.head.appendChild(el);
      }
      el.setAttribute("content", value);
    };

    if (description) {
      upsertMeta('meta[name="description"]', "name", "description", description);
      upsertMeta('meta[property="og:description"]', "property", "og:description", description);
    }
    if (keywordsStr) {
      upsertMeta('meta[name="keywords"]', "name", "keywords", keywordsStr);
    }
    if (title) {
      upsertMeta('meta[property="og:title"]', "property", "og:title", title);
    }
    if (path) {
      let canon = document.querySelector('link[rel="canonical"]');
      if (!canon) {
        canon = document.createElement("link");
        canon.setAttribute("rel", "canonical");
        document.head.appendChild(canon);
      }
      const origin = (typeof window !== "undefined") ? window.location.origin : "";
      canon.setAttribute("href", `${origin}${path}`);
    }

    return () => {
      if (prev.title) document.title = prev.title;
      const setBack = (sel, key, val) => {
        const el = document.querySelector(sel);
        if (el && val !== undefined) el.setAttribute("content", val);
      };
      setBack('meta[name="description"]', "description", prev.desc);
      setBack('meta[name="keywords"]', "keywords", prev.keys);
      setBack('meta[property="og:title"]', "og:title", prev.og);
      setBack('meta[property="og:description"]', "og:description", prev.ogd);
      if (prev.canon) {
        const canon = document.querySelector('link[rel="canonical"]');
        if (canon) canon.setAttribute("href", prev.canon);
      }
    };
  }, [title, description, keywordsStr, path]);
};
